import React from 'react';

/**
 * Custom illustrated thumbnail for LexifAI (AI/OCR chatbot that reads documents
 * and answers questions about them). Same visual language as NOTEorious: white
 * canvas, #121110 ink only. Depicts a document being read by a magnifier (OCR),
 * feeding a question/answer chat exchange. SMIL animations run because the card
 * lives inside `.op-page`.
 */
const INK = '#121110';

const LexifaiThumb: React.FC = () => (
  <svg
    viewBox="0 0 400 300"
    className="op-thumb w-full h-full bg-white transition-transform duration-700 ease-out group-hover:scale-105"
    preserveAspectRatio="xMidYMid meet"
    role="img"
    aria-label="LexifAI — OCR document Q&A"
  >
    {/* Canvas */}
    <rect width="400" height="300" fill="#ffffff" />

    {/* Faint dotted texture */}
    <g fill={INK} opacity="0.05">
      {Array.from({ length: 6 }).map((_, r) =>
        Array.from({ length: 9 }).map((_, c) => (
          <circle key={`${r}-${c}`} cx={22 + c * 45} cy={26 + r * 48} r="1.4" />
        ))
      )}
    </g>

    {/* Document sheet */}
    <g>
      <rect x="40" y="54" width="140" height="192" rx="10" fill="#ffffff" stroke={INK} strokeWidth="1.8" />
      {/* title */}
      <rect x="56" y="74" width="66" height="9" rx="4" fill={INK} />
      {/* lines */}
      <rect x="56" y="96" width="104" height="6" rx="3" fill={INK} opacity="0.13" />
      <rect x="56" y="110" width="88" height="6" rx="3" fill={INK} opacity="0.13" />
      <rect x="56" y="124" width="100" height="6" rx="3" fill={INK} opacity="0.13" />
      {/* highlighted / "read" line */}
      <rect x="52" y="136" width="86" height="12" rx="3" fill={INK} opacity="0.12" />
      <rect x="56" y="139" width="74" height="6" rx="3" fill={INK} opacity="0.7" />
      {/* more lines */}
      <rect x="56" y="160" width="96" height="6" rx="3" fill={INK} opacity="0.13" />
      <rect x="56" y="174" width="82" height="6" rx="3" fill={INK} opacity="0.13" />
      <rect x="56" y="188" width="100" height="6" rx="3" fill={INK} opacity="0.13" />
      <rect x="56" y="212" width="72" height="6" rx="3" fill={INK} opacity="0.13" />
    </g>

    {/* Magnifier reading the document */}
    <g>
      {/* handle */}
      <line x1="170" y1="196" x2="188" y2="214" stroke={INK} strokeWidth="5" strokeLinecap="round" />
      {/* lens */}
      <circle cx="150" cy="176" r="32" fill="#ffffff" stroke={INK} strokeWidth="2.4" />
      {/* magnified text inside lens */}
      <rect x="132" y="168" width="36" height="5" rx="2.5" fill={INK} opacity="0.8" />
      <rect x="132" y="178" width="28" height="5" rx="2.5" fill={INK} opacity="0.5" />
      {/* scanning pulse ring */}
      <circle cx="150" cy="176" r="32" fill="none" stroke={INK} strokeWidth="1.5" opacity="0">
        <animate attributeName="r" values="10;40" dur="2.8s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.5;0" dur="2.8s" repeatCount="indefinite" />
      </circle>
    </g>

    {/* Dotted flow: document -> Q&A */}
    <path d="M186 120 C214 104 224 100 236 100" fill="none" stroke={INK} strokeWidth="1.4" strokeDasharray="2 5" strokeLinecap="round" opacity="0.35" />

    {/* Question bubble (outlined) */}
    <g>
      <rect x="234" y="80" width="126" height="42" rx="13" fill="#ffffff" stroke={INK} strokeWidth="1.5" />
      <rect x="248" y="94" width="72" height="6" rx="3" fill={INK} opacity="0.18" />
      <rect x="248" y="106" width="56" height="6" rx="3" fill={INK} opacity="0.18" />
      {/* question mark */}
      <text x="342" y="108" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="20" fontWeight="700" fill={INK}>?</text>
    </g>

    {/* Answer bubble (filled) */}
    <g>
      <rect x="234" y="140" width="126" height="66" rx="14" fill={INK} />
      {/* AI sparkle */}
      <path d="M252 156 C253 160.5 253.6 161 258 162 C253.6 163 253 163.5 252 168 C251 163.5 250.4 163 246 162 C250.4 161 251 160.5 252 156 Z" fill="#ffffff">
        <animate attributeName="opacity" values="1;0.4;1" dur="2.2s" repeatCount="indefinite" />
      </path>
      <rect x="266" y="158" width="80" height="6" rx="3" fill="#ffffff" opacity="0.85" />
      <rect x="248" y="174" width="98" height="6" rx="3" fill="#ffffff" opacity="0.6" />
      <rect x="248" y="188" width="78" height="6" rx="3" fill="#ffffff" opacity="0.6" />
    </g>
  </svg>
);

export default LexifaiThumb;
